import {
    RetrieveCategoryWithDepth,
    RetrieveProductTemplate,
} from '@ortros/stoux-lib'

import { ListProductByTemplateRes } from 'stoux-shared-types/services/product-template'

import axios from '@/core/axios'
import { ProductTemplateRepository } from '../repositories/product-template'
import { WithBarcodeSchema } from '../schemas/get-product-template-by-handle.schema'
import { GetProductTemplateCategoriesSchema } from '../schemas/get-product-template-categories.schema'
import { GetProductTemplateProductsSchema } from '../schemas/get-product-template-products.schema'
import StockAlert from 'stoux-shared-types/models/stock-alert'
import CustomerFavorite from 'stoux-shared-types/models/customer-favorite'

type InjectedDependencies = {
    productTemplateRepository: ProductTemplateRepository
}

export class ProductTemplateService {
    private readonly _productTemplateRepository: ProductTemplateRepository

    constructor({ productTemplateRepository }: InjectedDependencies) {
        this._productTemplateRepository = productTemplateRepository
    }

    public listStaticGenTemplates() {
        return this._productTemplateRepository.listStaticGenTemplates()
    }

    public async getProductTemplateById(id: string) {
        return this._productTemplateRepository.getProductTemplateById(id)
    }

    public async retrieveByBarcode(barcode: string) {
        const validatedDto = await WithBarcodeSchema.safeParseAsync({
            barcode,
        })

        if (!validatedDto.success) {
            throw new Error('Error while validating barcode.')
        }

        const { data } = await axios.get<{ template: RetrieveProductTemplate }>(
            `/templates/${barcode}`,
        )
        return data.template
    }

    public async getProductTemplateProducts(
        barcode: string,
    ): Promise<ListProductByTemplateRes> {
        const validatedDto =
            await GetProductTemplateProductsSchema.safeParseAsync({
                barcode,
            })

        if (!validatedDto.success) {
            throw new Error('Error while validating barcode.')
        }

        return this._productTemplateRepository.getProductTemplateProducts(
            barcode,
        )
    }

    public async getProductTemplateCategories(
        handle: string,
    ): Promise<RetrieveCategoryWithDepth[]> {
        const validatedDto =
            await GetProductTemplateCategoriesSchema.safeParseAsync({ handle })

        if (!validatedDto.success) {
            throw new Error('Error while validating handle.')
        }

        return this._productTemplateRepository.getProductTemplateCategories(
            handle,
        )
    }

    public async getNewProductTemplates(): Promise<RetrieveProductTemplate[]> {
        return this._productTemplateRepository.getNewProductTemplates()
    }

    public async createStockAlert(id: string) {
        const { data } = await axios.post('/alerts', {
            templateId: id,
        })

        return data
    }

    public async listStockAlerts(): Promise<{
        count: number
        alerts: StockAlert[]
    }> {
        const { data } = await axios.get('/alerts')
        return data as { count: number; alerts: StockAlert[] }
    }

    public async deleteStockAlert(id: string): Promise<void> {
        await axios.delete(`/alerts/${id}`)
    }

    public async createFavorite(templateId: string) {
        const { data } = await axios.post('/favorites', {
            templateId,
        })

        return data
    }

    public async listFavorites(): Promise<{
        count: number
        favorites: CustomerFavorite[]
    }> {
        const { data } = await axios.get('/favorites')
        return data as { count: number; favorites: CustomerFavorite[] }
    }

    public async deleteFavorite(favoriteId: string): Promise<void> {
        await axios.delete(`/favorites/${favoriteId}`)
    }
}
